import { ApolloError, gql } from '@apollo/client';
import { useMutation } from 'platform/useMutation';
import { useObservability } from 'shared/helpers/observability';
import {
  SubmitSSVAByRecordedFeature,
  SubmitSSVAByRecordedFeatureVariables,
} from 'sellDirect/graphql/__generated__/SubmitSSVAByRecordedFeature';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { OdProtosWebData_RecordedFeature } from '__generated__/globalTypes';
import { VirtualAssessmentQuery } from 'sellDirect/graphql/useVirtualAssessmentData';
import { getHubOverviewRoute, getHubSSVAUploadRoute } from 'shared/helpers/routes';
import { useHistory, generatePath } from 'react-router-dom';

const SUBMIT_SSVA_BY_RECORDED_FEATURE = gql`
  mutation SubmitSSVAByRecordedFeature(
    $sellerInputUUID: String!
    $recordedFeature: OdProtosWebData_RecordedFeature!
  ) {
    sellDirect {
      submitSSVARecordedFeature(
        input: {
          recordedFeature: $recordedFeature
          lead: { source: { type: RECEPTION, id: $sellerInputUUID } }
        }
      ) {
        virtualAssessment {
          id
          ssvaRecordingsSubmitted
          ssvaQuestionnaireSubmitted
          ssvaInteriorRecordingsSubmitted
          ssvaFrontyardRecordingsSubmitted
          ssvaBackyardRecordingsSubmitted
          ssvaRecordings {
            id
            recordedFeature
            s3Directory
            fileName
          }
        }
      }
    }
  }
`;

/** Make GraphQL request to submit SSVA by RecordedFeature */
export const useSubmitSSVAByRecordedFeature = () => {
  const { sellerInputUUID, addressUUID } = useQualificationContext();
  const { trackError } = useObservability();
  const history = useHistory();

  const onError = (error: ApolloError) => {
    if (error) {
      trackError('submit-ssva-by-recorded-feature', error);
      window.location.assign(generatePath(getHubOverviewRoute(), { addressUUID }));
    }
  };

  const [submitSSVAWebMutation, submitSSVAWebMutationResp] = useMutation<
    SubmitSSVAByRecordedFeature,
    SubmitSSVAByRecordedFeatureVariables
  >(SUBMIT_SSVA_BY_RECORDED_FEATURE, {
    awaitRefetchQueries: true,
    fetchPolicy: 'no-cache',
    refetchQueries: [{ query: VirtualAssessmentQuery, variables: { sellerInputUUID } }],
    onError,
  });

  const submitSSVAByRecordedFeature = (feature: OdProtosWebData_RecordedFeature) => {
    return submitSSVAWebMutation({
      variables: { sellerInputUUID, recordedFeature: feature },
    })
      .then(({ data }) => {
        if (!data?.sellDirect.submitSSVARecordedFeature) return;
        history.replace(generatePath(getHubSSVAUploadRoute(), { addressUUID }));
      })
      .catch(onError);
  };

  return {
    submitSSVAByRecordedFeature,
    data: submitSSVAWebMutationResp.data,
    loading: submitSSVAWebMutationResp.loading,
    error: submitSSVAWebMutationResp.error,
  };
};
